/**
 * Welcome-Kampagne — weekly pick curation.
 *
 * The Monday cron proposes the top-10 past-6M FT PLX mailings by CPA-ETKP,
 * each with a two-layer staleness verdict (regex date anchors + LLM semantic
 * events). Marketing selects and orders the mailings of the welcome series
 * here; manual adds of arbitrary PLX mailings are possible. The send pipeline
 * uses the latest week that has selections — saving here IS the go-live for
 * next enrollments.
 */

import {
  Add,
  BlockOutlined,
  CheckCircleOutline,
  EditNoteOutlined,
  ErrorOutline,
  Refresh,
  SmartToyOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import InfoTip from '../../components/InfoTip';
import { api } from '../../helpers/apiUtils';
import EvaluationTab from './EvaluationTab';
import ReportFlowsTab from './ReportFlowsTab';

interface ScreeningReason {
  source: 'regex' | 'llm';
  quote: string;
  why: string;
}

interface Pick {
  id: number;
  week_start: string;
  plx_mailing_email_id: number;
  proposal_rank: number | null;
  etkp: string | number | null;
  orders_count: number | null;
  cpa_revenue: string | number | null;
  recipients: number | null;
  ai_verdict: 'ok' | 'flagged' | 'error' | null;
  ai_reasons: string | null;
  ai_topic: string | null;
  is_manual_add: number;
  selected_position: number | null;
  approved_by: string | null;
  auto_verdict: 'selected' | 'skipped' | 'not_evaluated' | null;
  auto_reason: string | null;
  auto_detail: string | null;
  auto_position: number | null;
  auto_decided_at: string | null;
  subject: string;
  mailing_number: number;
  campaign_id: number;
  campaign_name: string;
  product_code: string;
  product_name: string;
}

interface ActivePick {
  pick_id: number;
  week_start: string;
  plx_mailing_email_id: number;
  selected_position: number;
  subject: string;
  campaign_name: string;
  product_code: string;
  approved_by: string | null;
}

/** Marker the weekly cron writes into approved_by when it picks the set itself. */
const AUTO_APPROVER = 'auto';

/**
 * Längste Serie, die die Automatik anstrebt (Backend: WELCOME_SET_SIZES = [5, 3]).
 * Die Automatik füllt 5 wenn 5 Kandidaten qualifizieren, sonst 3 — manuell sind
 * beliebige Positionen bis hierher belegbar.
 */
const MAX_POSITIONS = 5;
const POSITION_OPTIONS = Array.from({ length: MAX_POSITIONS }, (_, i) => i + 1);

/** Spalten der Vorschlagstabelle samt Erklärung — je ein Satz, ohne Fachjargon. */
const COLUMN_HINTS: { label: string; tip: string; align?: 'right' }[] = [
  {
    label: 'Rang',
    tip: 'Platz im Vorschlag, sortiert nach Erlös pro 1.000 Empfänger. „Manuell“ heißt: von Hand ergänzt, nicht aus dem automatischen Vorschlag.',
  },
  { label: 'Betreff', tip: 'Die Betreffzeile, die der neue Abonnent im Postfach sieht.' },
  {
    label: 'Kampagne',
    tip: 'Name des Direkt-Mailings, aus dem diese Mail stammt — so ist sie im Direkt-Mailing-Bereich wiederzufinden.',
  },
  {
    label: 'Produkt',
    tip: 'Das beworbene Produkt. Zwei Positionen mit demselben Produkt sind erlaubt, müssen aber bestätigt werden.',
  },
  {
    label: 'Thema',
    tip: 'Der inhaltliche Winkel, den die KI aus dem Text erkannt hat. Dient nur dazu, zwei zu ähnliche Mails in derselben Serie zu vermeiden.',
  },
  {
    label: 'ETKP €',
    align: 'right',
    tip: 'Erlös pro 1.000 Empfänger — macht unterschiedlich große Aussendungen vergleichbar. Gerechnet mit dem CPA-Wert pro Bestellung, nicht mit dem Rechnungsbetrag.',
  },
  {
    label: 'Orders',
    align: 'right',
    tip: 'Bestellungen, die diesem Mailing in den letzten 6 Monaten zugerechnet wurden. Zu wenige Bestellungen führen dazu, dass die Automatik das Mailing überspringt — die Zahl wäre sonst Zufall.',
  },
  {
    label: 'Empfänger',
    align: 'right',
    tip: 'Wie viele Personen dieses Mailing in den letzten 6 Monaten insgesamt bekommen haben. Die Bezugsgröße für den ETKP.',
  },
  {
    label: 'KI-Prüfung',
    tip: 'Prüft, ob der Text noch aktuell ist — Mailings mit Datumsbezug oder tagesaktuellem Aufhänger veralten schnell. „Veraltet?“ heißt: erst im Direkt-Mailing-Editor auffrischen, dann hier mit ↻ neu prüfen.',
  },
  {
    label: 'Automatik',
    tip: 'Wie die Automatik über diese Zeile entschieden hat. Bei „übersprungen“ steht der Grund im Tooltip des Chips.',
  },
  {
    label: 'Position',
    tip: 'An welcher Stelle der Serie diese Mail verschickt wird. Leer lassen heißt: nicht Teil der Serie.',
  },
];

/**
 * Erklärung der Kampagne für Marketing. Bewusst ohne Fachbegriffe und als
 * Konstante gehalten, damit die Formulierungen an einer Stelle gepflegt werden.
 */
const WELCOME_EXPLAINER: ExplainerSection[] = [
  {
    label: 'Was hier passiert',
    body: 'Wer sich neu für den Finanztrends-Newsletter anmeldet, bekommt automatisch eine feste Reihe von Mails — die erste sofort, die weiteren an den Folgetagen. Diese Seite legt fest, WELCHE Mails das sind. Die Serie ist 3 oder 5 Mails lang.',
  },
  {
    label: 'Woher die Mailings kommen',
    body: 'Aus den Direkt-Mailings der letzten 6 Monate. Es wird nichts neu geschrieben, sondern ausgewählt: das System schlägt montags die 10 erfolgreichsten vor, sortiert nach Erlös. Mailings derselben Serie und mit gleichem Betreff sind dabei schon zusammengefasst.',
  },
  {
    label: 'Was die Spalte ETKP bedeutet',
    body: 'Erlös pro 1.000 Empfänger — die Vergleichszahl zwischen unterschiedlich großen Aussendungen. Gerechnet wird pro Bestellung mit dem CPA-Wert (40 % des Anfo-Werts), nicht mit dem Rechnungsbetrag: der steht bei jungen Kampagnen noch gar nicht fest und würde gute Mailings künstlich schlechtrechnen.',
  },
  {
    label: 'Wer auswählt',
    body: 'Montags um 05:00 entscheidet das System selbst. Aufgenommen wird nur, was die KI-Prüfung bestanden hat, genug Bestellungen vorweist und sich in Betreff, Thema und Produkt von den anderen Positionen unterscheidet. Das Ranking wird von oben abgearbeitet und endet, sobald alle Positionen belegt sind — ein hoher ETKP allein reicht also nicht, er bestimmt nur die Reihenfolge. Die Spalte „Automatik“ zeigt je Zeile, wie entschieden wurde; bei übersprungenen Zeilen steht der Grund im Tooltip. Eine Auswahl von Hand überschreibt die Automatik jederzeit.',
  },
  {
    label: 'Warum die Mails sich unterscheiden müssen',
    body: 'Die Serie kommt an aufeinanderfolgenden Tagen an. Zweimal derselbe Betreff wird deshalb hart blockiert — der Abonnent bekäme sichtbar dieselbe Mail zweimal. Gleiches Thema oder Produkt ist erlaubt, muss aber bestätigt werden. Ein Mailing mit dem Hinweis „veraltet?“ erst im Direkt-Mailing-Editor auffrischen und danach hier mit ↻ neu prüfen.',
  },
  {
    label: 'Eigene Copy und A/B-Test',
    body: '„Eigene Welcome-Copy anlegen“ erzeugt eine Kopie des aktuell gewählten Mailings — Produkt, Landing-Page und Gestaltung bleiben, geändert werden nur Betreff und Text. Sie wird inaktiv angelegt und geht erst raus, wenn im Editor der Haken „Copy aktiv“ gesetzt ist; damit lässt sie sich später auch pausieren, ohne sie zu löschen. Ohne A/B-Haken ersetzt sie die automatische Auswahl auf ihrer Position vollständig. Mit Haken bekommt die Hälfte der Neuanmeldungen die eigene Copy, die andere Hälfte weiter das automatisch gewählte Mailing — so lässt sich messen, ob der neue Text besser läuft.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Jede Änderung hier wirkt nur für NEUE Anmeldungen. Wer schon in der Serie steckt, bekommt sie mit dem alten Stand zu Ende. Bei einem A/B-Test steht außerdem von der Anmeldung an fest, welche Hälfte jemand bekommt — das ändert sich später nie mehr.',
  },
];

interface CustomCopy {
  campaign_id: number;
  campaign_name: string;
  /** Schalter im Editor — false = angelegt, geht aber nicht raus. */
  copy_active: boolean;
  welcome_position: number;
  welcome_ab_test: boolean;
  status: string;
  product_code: string;
  plx_mailing_email_id: number | null;
  subject: string | null;
  active: boolean;
  series_length: number;
}

interface SearchResult {
  id: number;
  subject: string;
  mailing_number: number;
  campaign_name: string;
  product_code: string;
  campaign_status: string;
}

const parseReasons = (raw: string | null): ScreeningReason[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const VerdictChip: FC<{ pick: Pick }> = ({ pick }) => {
  const reasons = parseReasons(pick.ai_reasons);
  const tooltip =
    reasons.length > 0 ? (
      <Box>
        {reasons.map((reason, index) => (
          <Box key={index} sx={{ mb: 0.5 }}>
            {reason.quote && (
              <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                „{reason.quote}“
              </Typography>
            )}
            <Typography variant="caption">
              [{reason.source === 'llm' ? 'KI' : 'Datum'}] {reason.why}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : (
      ''
    );

  if (pick.ai_verdict === 'ok') {
    return <Chip size="small" color="success" icon={<CheckCircleOutline />} label="OK" />;
  }
  if (pick.ai_verdict === 'flagged') {
    return (
      <Tooltip title={tooltip} placement="left">
        <Chip
          size="small"
          color="warning"
          icon={<WarningAmberOutlined />}
          label={`Veraltet? (${reasons.length})`}
        />
      </Tooltip>
    );
  }
  if (pick.ai_verdict === 'error') {
    return (
      <Tooltip title={tooltip || 'Prüfung fehlgeschlagen — manuell prüfen'} placement="left">
        <Chip size="small" color="error" icon={<ErrorOutline />} label="Prüfung fehlgeschlagen" />
      </Tooltip>
    );
  }
  return <Chip size="small" label="Ausstehend" />;
};

/**
 * Why the weekly Automatik chose or passed over this row. The verdict is
 * RECORDED at decision time (welcome_campaign_picks.auto_*), not recomputed on
 * read, so the table shows what actually happened rather than what the picker
 * would decide against today's numbers.
 */
const AutoDecisionChip: FC<{ pick: Pick }> = ({ pick }) => {
  if (!pick.auto_verdict) {
    return (
      <Tooltip
        arrow
        placement="left"
        title="Für diese Woche liegt keine Automatik-Entscheidung vor — der Vorschlag wurde nie automatisch bewertet."
      >
        <Typography variant="body2" color="text.secondary">
          –
        </Typography>
      </Tooltip>
    );
  }

  if (pick.auto_verdict === 'selected') {
    return (
      <Tooltip
        arrow
        placement="left"
        title="Von der Automatik gewählt: KI-Prüfung „OK“, genügend Orders, und Betreff / Thema / Produkt unterscheiden sich von den anderen Positionen."
      >
        <Chip
          size="small"
          color="success"
          icon={<CheckCircleOutline />}
          label={`Gewählt — Pos. ${pick.auto_position ?? '?'}`}
        />
      </Tooltip>
    );
  }

  if (pick.auto_verdict === 'not_evaluated') {
    return (
      <Tooltip
        arrow
        placement="left"
        title="Nicht mehr geprüft: alle Positionen waren bereits belegt, bevor diese Zeile an der Reihe war. Die Automatik geht das Ranking von oben nach unten durch und hört auf, sobald das Set voll ist."
      >
        <Chip size="small" variant="outlined" label="Nicht mehr geprüft" />
      </Tooltip>
    );
  }

  // skipped — the interesting case
  const detail = pick.auto_detail ?? '';
  const skipLabels: Record<string, { label: string; why: string; color?: 'warning' | 'error' }> = {
    topic_clash: {
      label: 'Thema doppelt',
      why: `Thema „${detail}“ ist bereits durch eine höher platzierte Position belegt. Ein neuer Abonnent bekommt alle Mails der Serie an aufeinanderfolgenden Tagen — zwei Mailings zum selben Winkel wären an zwei Tagen praktisch dasselbe Angebot.`,
      color: 'warning',
    },
    product_clash: {
      label: 'Produkt doppelt',
      why: `Produkt ${detail} ist bereits durch eine höher platzierte Position belegt. Die Serie soll möglichst durchgehend verschiedene Produkte bewerben.`,
      color: 'warning',
    },
    duplicate_subject: {
      label: 'Betreff doppelt',
      why: 'Ein höher platziertes Mailing hat (nach Normalisierung von Jahreszahlen und {DATE:…}-Platzhaltern) denselben Betreff. Wird nie automatisch übergangen — derselbe Betreff zweimal wäre für den Abonnenten sichtbar dieselbe Mail.',
      color: 'error',
    },
    ai_not_ok: {
      label:
        detail === 'error'
          ? 'KI-Prüfung fehlgeschlagen'
          : detail === 'unscreened'
            ? 'Noch nicht geprüft'
            : 'KI-Prüfung: veraltet',
      why:
        detail === 'error'
          ? 'Die KI-Prüfung lief auf einen Fehler. Ohne gültiges Urteil wählt die Automatik nicht aus — bitte per ↻ erneut prüfen.'
          : detail === 'unscreened'
            ? 'Für dieses Mailing liegt noch kein KI-Urteil vor. Die Automatik wählt nur aus geprüften Vorschlägen.'
            : 'Die KI-Prüfung hat zeitgebundene Inhalte gefunden (siehe Spalte KI-Prüfung). Welcome-Mails gehen noch Monate später raus — die Automatik wählt deshalb nur „OK“-Mailings. Copy im Direkt-Mailing-Editor auffrischen, dann hier neu prüfen.',
      color: detail === 'error' ? 'error' : 'warning',
    },
    below_min_orders: {
      label: 'Zu wenig Orders',
      why: `Nur ${detail} Orders im Ranking-Zeitraum. Bei so wenigen Bestellungen ist der ETKP weitgehend Zufall, deshalb wählt die Automatik solche Mailings nicht — manuell ist es weiterhin möglich.`,
    },
    missing_topic: {
      label: 'Kein Thema erkannt',
      why: 'Die KI-Prüfung hat kein Themen-Label geliefert. Ohne Thema lässt sich die Themen-Vielfalt nicht prüfen, und ein fehlendes Label deutet auf eine unvollständige Prüfung hin — per ↻ erneut prüfen.',
    },
    set_incomplete: {
      label: 'Set unvollständig',
      why: 'Dieses Mailing hätte alle Kriterien erfüllt, aber es kamen weder 5 noch 3 passende Mailings zusammen. Die Automatik gibt dann gar nichts frei und lässt das bisherige Set weiterlaufen.',
    },
    manual_add: {
      label: 'Manuell ergänzt',
      why: 'Manuell hinzugefügte Mailings werden von der Automatik nie selbst gewählt — sie stehen bewusst für die manuelle Auswahl bereit.',
    },
  };

  const entry = skipLabels[pick.auto_reason ?? ''] ?? {
    label: pick.auto_reason ?? 'Übersprungen',
    why: 'Von der Automatik übersprungen.',
  };

  return (
    <Tooltip arrow placement="left" title={entry.why}>
      <Chip
        size="small"
        variant="outlined"
        color={entry.color}
        icon={<BlockOutlined />}
        label={entry.label}
      />
    </Tooltip>
  );
};

const formatNumber = (value: string | number | null): string => {
  if (value === null || value === undefined) return '–';
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('de-DE') : '–';
};

const WelcomeCampaignPage: FC = () => {
  const [tab, setTab] = useState(0);
  const [weekStart, setWeekStart] = useState<string>('');
  const [weeks, setWeeks] = useState<string[]>([]);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [activeSet, setActiveSet] = useState<ActivePick[]>([]);
  const [selection, setSelection] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [rescreeningId, setRescreeningId] = useState<number | null>(null);
  const [customCopies, setCustomCopies] = useState<CustomCopy[]>([]);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyPosition, setCopyPosition] = useState<number | ''>('');
  const [copyAbTest, setCopyAbTest] = useState(false);
  const [cloning, setCloning] = useState(false);

  const normalizeWeek = (value: string): string => value.slice(0, 10);

  const loadPicks = useCallback(async (week?: string) => {
    setLoading(true);
    try {
      const response = await api.get('/api/welcome-campaign/picks', {
        params: week ? { week } : {},
      });
      const data = response.data.data;
      setWeekStart(normalizeWeek(data.week_start));
      setWeeks(data.weeks.map((w: { week_start: string }) => normalizeWeek(w.week_start)));
      setPicks(data.picks);
      setActiveSet(data.activeSet);
      const preset: Record<number, number> = {};
      for (const pick of data.picks as Pick[]) {
        if (pick.selected_position) preset[pick.id] = pick.selected_position;
      }
      setSelection(preset);
    } catch (error) {
      console.error('Failed to load picks', error);
      setMessage('Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPicks();
  }, [loadPicks]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/api/welcome-campaign/custom-copies');
        if (!cancelled) setCustomCopies(response.data.data ?? []);
      } catch (error) {
        console.error('Failed to load custom copies', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectionValid = useMemo(() => {
    const positions = Object.values(selection);
    return new Set(positions).size === positions.length && positions.length <= MAX_POSITIONS;
  }, [selection]);

  const handlePositionChange = (pickId: number, position: number | '') => {
    setSelection((previous) => {
      const next = { ...previous };
      if (position === '') {
        delete next[pickId];
        return next;
      }
      // A position can only be held by one pick — displace the old holder.
      for (const [otherId, otherPosition] of Object.entries(next)) {
        if (otherPosition === position) delete next[Number(otherId)];
      }
      next[pickId] = position;
      return next;
    });
  };

  const handleSave = async (confirmDiversity = false) => {
    // Empty selection is a deliberate clear operation (backend accepts it) —
    // gate it behind a confirm since the active set drives live sends.
    if (
      Object.keys(selection).length === 0 &&
      !window.confirm(
        'Auswahl der Woche wirklich leeren? Neue Anmeldungen erhalten dann das Set der Vorwoche — oder gar keine Welcome-Mails, wenn keins existiert.',
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      const selections = Object.entries(selection).map(([pickId, position]) => ({
        pick_id: Number(pickId),
        position,
      }));
      const response = await api.post('/api/welcome-campaign/picks/select', {
        week_start: weekStart,
        selections,
        confirm_diversity: confirmDiversity,
      });
      setActiveSet(response.data.data.activeSet);
      setMessage('Auswahl gespeichert — gilt für alle neuen Anmeldungen');
      await loadPicks(weekStart);
    } catch (error: any) {
      console.error('Failed to save selection', error);
      const data = error?.response?.data;
      // Diversity soft-guard: server asks for confirmation — marketing keeps
      // the final word, but never overlaps topics/products blindly.
      if (data?.requires_confirmation && !confirmDiversity) {
        setSaving(false);
        if (window.confirm(data.error)) {
          await handleSave(true);
        }
        return;
      }
      // Surface the server's reason (e.g. duplicate-subject guard) instead of
      // a generic failure.
      setMessage(data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleRecompute = async () => {
    setLoading(true);
    try {
      await api.post('/api/welcome-campaign/recompute');
      setMessage('Ranking neu berechnet');
      await loadPicks(weekStart);
    } catch (error) {
      console.error('Recompute failed', error);
      setMessage('Neuberechnung fehlgeschlagen');
      setLoading(false);
    }
  };

  const handleRescreen = async (pickId: number) => {
    setRescreeningId(pickId);
    try {
      await api.post(`/api/welcome-campaign/picks/${pickId}/rescreen`);
      await loadPicks(weekStart);
    } catch (error) {
      console.error('Rescreen failed', error);
      setMessage('Neuprüfung fehlgeschlagen');
    } finally {
      setRescreeningId(null);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await api.get('/api/welcome-campaign/mailing-search', {
        params: { q: query.trim() },
      });
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('Search failed', error);
    } finally {
      setSearching(false);
    }
  };

  const handleManualAdd = async (mailingEmailId: number) => {
    try {
      await api.post('/api/welcome-campaign/picks/manual-add', {
        week_start: weekStart,
        plx_mailing_email_id: mailingEmailId,
      });
      setAddDialogOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setMessage('Mailing hinzugefügt und geprüft');
      await loadPicks(weekStart);
    } catch (error) {
      console.error('Manual add failed', error);
      setMessage('Hinzufügen fehlgeschlagen');
    }
  };

  /**
   * Legt eine eigene Welcome-Copy an, indem der aktive Auto-Pick der Position
   * als Vorlage geklont wird — Produkt, Landing-URL, WKZ, Konzept, Absender und
   * Text kommen mit, geändert werden nur Betreff und Fließtext. Danach direkt in
   * den PLX-Editor, damit Marketing dort weiterarbeitet statt eine Kampagne von
   * Hand zusammenzusuchen.
   */
  const handleCreateCustomCopy = async () => {
    if (copyPosition === '') return;
    setCloning(true);
    try {
      const response = await api.post('/api/welcome-campaign/custom-copies', {
        position: copyPosition,
        ab_test: copyAbTest,
      });
      const campaignId = response.data?.data?.campaign_id;
      if (!campaignId) throw new Error('Keine campaign_id erhalten');
      window.location.href = `/plx-mailings?tab=editor&id=${campaignId}`;
    } catch (error: any) {
      console.error('Create custom copy failed', error);
      setMessage(error?.response?.data?.error ?? 'Anlegen fehlgeschlagen');
      setCloning(false);
    }
  };

  const selectedCount = Object.keys(selection).length;

  return (
    <Box m="20px">
      <Header
        title="Welcome-Kampagne"
        subtitle="Wöchentliche Auswahl der besten PLX-Mailings (nach CPA-ETKP) für neue Abonnenten"
      />

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Kuration" />
        <Tab label="Überblick" />
        <Tab label="Details" />
        <Tab label="Report-Strecken" />
      </Tabs>

      {/*
        Überblick und Details sind BEWUSST derselbe Komponententyp an derselben
        Stelle im Kinderbaum. React gleicht dann ab, statt neu zu montieren — der
        Fetch in `EvaluationTab` läuft einmal beim Mount, und das Umschalten
        zwischen den beiden Reitern löst deshalb KEINE zweite Anfrage aus (der
        Endpoint braucht ~4 s). Wer das hier in zwei getrennte Zweige aufteilt
        (`{tab===1 && <A/>}{tab===2 && <B/>}` mit verschiedenen Komponenten),
        holt bei jedem Klick neu.
      */}
      {(tab === 1 || tab === 2) && <EvaluationTab view={tab === 1 ? 'overview' : 'details'} />}

      {tab === 3 && <ReportFlowsTab />}

      {tab === 0 && (
        <>
          {activeSet.length > 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Aktives Set (Woche {activeSet[0].week_start}):</strong>{' '}
              {activeSet[0].approved_by === AUTO_APPROVER ? (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<SmartToyOutlined />}
                  label="Automatisch gewählt"
                  sx={{ mr: 1 }}
                />
              ) : (
                activeSet[0].approved_by && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={activeSet[0].approved_by}
                    sx={{ mr: 1 }}
                  />
                )
              )}
              {activeSet.map((pick) => `#${pick.selected_position} ${pick.subject}`).join(' · ')}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Noch kein Set freigegeben — es werden keine Welcome-Mails versendet, bis hier eine
              Auswahl gespeichert wird.
            </Alert>
          )}

          {customCopies.length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Eigene Welcome-Copy hinterlegt:</strong>{' '}
              {customCopies.map((copy, index) => (
                <span key={copy.campaign_id}>
                  {index > 0 && ' · '}
                  Mail {copy.welcome_position}: {copy.subject || copy.campaign_name}{' '}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={copy.welcome_ab_test ? 'info' : 'default'}
                    label={copy.welcome_ab_test ? 'A/B gegen Automatik' : 'ersetzt Automatik'}
                    sx={{ mx: 0.5 }}
                  />
                  {!copy.copy_active && (
                    <Tooltip
                      arrow
                      title="Diese Copy ist angelegt, aber noch nicht scharfgeschaltet — es läuft weiter das automatisch gewählte Mailing. Im Editor den Haken „Copy aktiv“ setzen, sobald der Text steht."
                    >
                      <Chip
                        size="small"
                        color="default"
                        variant="outlined"
                        label="nicht scharfgeschaltet"
                        sx={{ mx: 0.5 }}
                      />
                    </Tooltip>
                  )}
                  {copy.copy_active && !copy.active && (
                    <Tooltip
                      arrow
                      title={`Die Serie ist derzeit nur ${copy.series_length} Mails lang — Position ${copy.welcome_position} existiert nicht und wird nicht versendet. Die Automatik füllt 5 Positionen erst, wenn 5 Kandidaten qualifizieren.`}
                    >
                      <Chip
                        size="small"
                        color="warning"
                        label="Position inaktiv"
                        sx={{ mx: 0.5 }}
                      />
                    </Tooltip>
                  )}
                  <Button size="small" href={`/plx-mailings?tab=editor&id=${copy.campaign_id}`}>
                    bearbeiten
                  </Button>
                </span>
              ))}
            </Alert>
          )}

          <Box display="flex" gap={2} alignItems="center" mb={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Woche</InputLabel>
              <Select
                value={weeks.includes(weekStart) ? weekStart : ''}
                label="Woche"
                onChange={(event) => loadPicks(event.target.value)}
              >
                {weeks.map((week) => (
                  <MenuItem key={week} value={week}>
                    KW ab {week}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<Refresh />} onClick={handleRecompute}>
              Ranking neu berechnen
            </Button>
            <Button variant="outlined" startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
              Mailing hinzufügen
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditNoteOutlined />}
              onClick={() => setCopyDialogOpen(true)}
            >
              Eigene Welcome-Copy anlegen
            </Button>
            <Box flexGrow={1} />
            <Button
              variant="contained"
              color="primary"
              disabled={saving || !selectionValid}
              onClick={() => handleSave()}
            >
              {saving
                ? 'Speichert…'
                : selectedCount === 0
                  ? 'Auswahl leeren'
                  : `Auswahl speichern (${selectedCount})`}
            </Button>
          </Box>

          {selectedCount > 0 && selectedCount < 3 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Es sind nur {selectedCount} Positionen belegt — die Welcome-Serie wird entsprechend
              kürzer versendet.
            </Alert>
          )}

          <Explainer
            title="Wie funktioniert die Welcome-Kampagne? — anklicken für Erklärung"
            sections={WELCOME_EXPLAINER}
          />

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMN_HINTS.map((column) => (
                      <TableCell key={column.label} align={column.align ?? 'inherit'}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          {column.label}
                          <InfoTip text={column.tip} />
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {picks.map((pick) => (
                    <TableRow key={pick.id} selected={selection[pick.id] !== undefined}>
                      <TableCell>
                        {pick.is_manual_add ? (
                          <Chip size="small" label="Manuell" variant="outlined" />
                        ) : (
                          pick.proposal_rank
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" noWrap title={pick.subject}>
                          {pick.subject}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={pick.campaign_name}>
                          {pick.campaign_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{pick.product_code}</TableCell>
                      <TableCell>
                        {pick.ai_topic ? (
                          <Chip size="small" variant="outlined" label={pick.ai_topic} />
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell align="right">{formatNumber(pick.etkp)}</TableCell>
                      <TableCell align="right">{formatNumber(pick.orders_count)}</TableCell>
                      <TableCell align="right">{formatNumber(pick.recipients)}</TableCell>
                      <TableCell>
                        <VerdictChip pick={pick} />
                      </TableCell>
                      <TableCell>
                        <AutoDecisionChip pick={pick} />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                          <Select
                            value={selection[pick.id] ?? ''}
                            displayEmpty
                            onChange={(event) =>
                              handlePositionChange(pick.id, event.target.value as number | '')
                            }
                          >
                            <MenuItem value="">–</MenuItem>
                            {POSITION_OPTIONS.map((position) => (
                              <MenuItem key={position} value={position}>
                                {position}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {pick.approved_by === AUTO_APPROVER && (
                          <Tooltip title="Vom wöchentlichen Cron automatisch gewählt — jederzeit überschreibbar">
                            <SmartToyOutlined fontSize="small" color="info" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="KI-Prüfung erneut ausführen">
                          <span>
                            <IconButton
                              size="small"
                              disabled={rescreeningId === pick.id}
                              onClick={() => handleRescreen(pick.id)}
                            >
                              {rescreeningId === pick.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Refresh fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {picks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        Keine Vorschläge für diese Woche — „Ranking neu berechnen“ ausführen.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>PLX-Mailing manuell hinzufügen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Suche (Betreff, Kampagne, Produkt)"
            value={searchQuery}
            onChange={(event) => handleSearch(event.target.value)}
          />
          {searching && <CircularProgress size={20} sx={{ mt: 1 }} />}
          {searchResults.map((result) => (
            <Box
              key={result.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={0.5}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap title={result.subject}>
                  {result.subject}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {result.campaign_name} · {result.product_code} · E-Mail #{result.mailing_number}
                </Typography>
              </Box>
              <Button size="small" onClick={() => handleManualAdd(result.id)}>
                Hinzufügen
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eigene Welcome-Copy anlegen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Für welche Mail der Serie soll ein eigener Text geschrieben werden? Die neue Kampagne
            übernimmt Produkt, Landing-URL und Gestaltung des aktuell gewählten Mailings — geändert
            werden müssen nur Betreff und Text. Anschließend geht es direkt in den Editor.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Die Copy wird <strong>inaktiv</strong> angelegt und geht noch nicht raus. Erst wenn der
            Text steht, im Editor den Haken „Copy aktiv“ setzen — sonst bekämen die ersten Empfänger
            noch den unveränderten Text der Vorlage.
          </Alert>
          {POSITION_OPTIONS.map((position) => {
            const source = activeSet.find((pick) => pick.selected_position === position);
            const taken = customCopies.find((copy) => copy.welcome_position === position);
            const disabled = !source || Boolean(taken);
            return (
              <Box
                key={position}
                onClick={() => !disabled && setCopyPosition(position)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: copyPosition === position ? 'primary.main' : 'divider',
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Radio size="small" checked={copyPosition === position} disabled={disabled} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">Mail {position} der Serie</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {taken
                      ? 'Bereits mit einer eigenen Copy belegt'
                      : source
                        ? `Vorlage: ${source.subject} · ${source.product_code}`
                        : `Die Serie ist derzeit nur ${activeSet.length} Mails lang — diese Position wird nicht versendet`}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={copyAbTest}
                onChange={(event) => setCopyAbTest(event.target.checked)}
              />
            }
            label="Als A/B-Test gegen die automatische Auswahl"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {copyAbTest
              ? 'Die Hälfte der neuen Abonnenten bekommt die eigene Copy, die andere Hälfte weiter das automatisch gewählte Mailing. Die Zuordnung ist je Empfänger fest.'
              : 'Ohne Haken ersetzt die eigene Copy das automatisch gewählte Mailing für alle neuen Abonnenten.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={copyPosition === '' || cloning}
            onClick={handleCreateCustomCopy}
          >
            {cloning ? 'Legt an…' : 'Anlegen und bearbeiten'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={message !== null}
        autoHideDuration={4000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Box>
  );
};

export default WelcomeCampaignPage;
